// The connection rule (docs/02-Game-Design/Core Mechanics.md, "Selecting a
// star"): candidates are the selected nodes nearest first (ties to the
// earliest selected); the first whose segment to the new node is legal is the
// anchor. A segment is legal when it crosses no existing line and overlaps no
// used node other than its endpoints. Plain nearest: no preference for the
// line that blocks fewer stars.
import { dist2, pointWithinOfSegment, segmentsCross } from '../geometry/segments';
import type { LevelNode } from '../level/schema';
import { blockRadius, derive, type DerivedState } from './state';
import type { Line, PlayState, SelectCode } from './types';

export interface SelectOutcome {
  code: SelectCode;
  state: PlayState;
  /** For refused_locked: the word that owns the star. */
  word?: string;
}

function segmentLegal(
  nodes: Map<number, LevelNode>,
  anchor: number,
  target: number,
  lines: readonly Line[],
  used: ReadonlySet<number>,
  radius: number,
): boolean {
  const a = nodes.get(anchor)!;
  const t = nodes.get(target)!;
  for (const [la, lb] of lines) {
    // A segment never crosses a line it shares an endpoint with. The target
    // is unused, so only the anchor can be shared.
    if (la === anchor || lb === anchor) continue;
    if (segmentsCross(a, t, nodes.get(la)!, nodes.get(lb)!)) return false;
  }
  for (const id of used) {
    if (id === anchor || id === target) continue;
    if (pointWithinOfSegment(nodes.get(id)!, a, t, radius)) return false;
  }
  return true;
}

/** The anchor the rule picks for `target`, or null when no candidate is legal. */
export function anchorFor(state: PlayState, target: number, derived: DerivedState = derive(state)): number | null {
  const { nodes, lines, used } = derived;
  const t = nodes.get(target)!;
  const ranked = state.selection
    .map((id, order) => ({ id, order, d: dist2(nodes.get(id)!, t) }))
    .sort((p, q) => p.d - q.d || p.order - q.order);
  const radius = blockRadius(state.level);
  for (const c of ranked) {
    if (segmentLegal(nodes, c.id, target, lines, used, radius)) return c.id;
  }
  return null;
}

/**
 * A tap on a node: select it, deselect it, rewind to it, or refuse. A caller
 * trying many taps from one state (the solver) passes `derive(state)` once.
 */
export function tapNode(state: PlayState, id: number, derived: DerivedState = derive(state)): SelectOutcome {
  const node = derived.nodes.get(id);
  if (!node) return { code: 'refused_blocked', state };
  const owner = derived.locked.get(id);
  if (owner !== undefined) return { code: 'refused_locked', state, word: owner };
  const at = state.selection.indexOf(id);
  if (at >= 0) {
    if (at === state.selection.length - 1) return { code: 'deselected', state: truncateSelection(state, at) };
    return { code: 'rewound', state: truncateSelection(state, at + 1) };
  }
  if (derived.blocked.has(id)) return { code: 'refused_blocked', state };
  const code: SelectCode = 'selected';
  if (state.selection.length === 0) {
    return { code, state: { ...state, selection: [id], selectionLines: [] } };
  }
  const anchor = anchorFor(state, id, derived);
  if (anchor === null) return { code: 'refused_no_line', state };
  return {
    code,
    state: { ...state, selection: [...state.selection, id], selectionLines: [...state.selectionLines, [anchor, id]] },
  };
}

/** Keep the first `count` selected nodes; drop the rest with their lines. */
function truncateSelection(state: PlayState, count: number): PlayState {
  return {
    ...state,
    selection: state.selection.slice(0, count),
    selectionLines: state.selectionLines.slice(0, Math.max(0, count - 1)),
  };
}

/** The Clear latest button: the same as tapping the most recent star. */
export function clearLatest(state: PlayState): SelectOutcome {
  if (state.selection.length === 0) return { code: 'cleared', state };
  return { code: 'deselected', state: truncateSelection(state, state.selection.length - 1) };
}

/** The Clear all button. */
export function clearAll(state: PlayState): SelectOutcome {
  return { code: 'cleared', state: { ...state, selection: [], selectionLines: [] } };
}
